import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Package, 
  Plus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  ShoppingCart,
  Calendar as CalendarIcon,
  User,
  Ear,
  DollarSign,
  FileText,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays, differenceInDays } from "date-fns";

const TRIAL_STATUSES = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "In Progress", color: "bg-green-100 text-green-700" },
  extended: { label: "Extended", color: "bg-amber-100 text-amber-700" },
  converted: { label: "Purchased", color: "bg-emerald-100 text-emerald-700" },
  returned: { label: "Returned", color: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-700" }
};

export default function TrialsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trials, setTrials] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [patients, setPatients] = useState([]);
  const [hearingAids, setHearingAids] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [activeTab, setActiveTab] = useState("trials");

  const [newTrial, setNewTrial] = useState({
    patient_id: "",
    clinic_id: "",
    hearing_aid_id: "",
    serial_number: "",
    ear: "both",
    trial_start_date: new Date().toISOString(),
    trial_days: 3,
    notes: ""
  });

  const [purchaseData, setPurchaseData] = useState({
    unit_price: 0,
    discount_percent: 0,
    zoho_invoice_number: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [trialsRes, purchasesRes, patientsRes, aidsRes, clinicsRes] = await Promise.all([
        apiClient.get("/trials"),
        apiClient.get("/purchases"),
        apiClient.get("/patients"),
        apiClient.get("/hearing-aids"),
        apiClient.get("/clinics")
      ]);
      setTrials(trialsRes.data);
      setPurchases(purchasesRes.data);
      setPatients(patientsRes.data);
      setHearingAids(aidsRes.data);
      setClinics(clinicsRes.data);
      
      if (clinicsRes.data.length > 0) {
        setNewTrial(prev => ({ ...prev, clinic_id: clinicsRes.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrial = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post("/trials", newTrial);
      setTrials([response.data, ...trials]);
      toast.success("Trial created successfully!");
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create trial");
    }
  };

  const handleConvertToPurchase = async () => {
    if (!selectedTrial) return;

    try {
      const aid = hearingAids.find(a => a.id === selectedTrial.hearing_aid_id);
      const payload = {
        patient_id: selectedTrial.patient_id,
        clinic_id: selectedTrial.clinic_id,
        hearing_aid_id: selectedTrial.hearing_aid_id,
        serial_number: selectedTrial.serial_number || `SN-${Date.now()}`,
        ear: selectedTrial.ear,
        quantity: 1,
        unit_price: purchaseData.unit_price || aid?.price || 0,
        discount_percent: purchaseData.discount_percent,
        zoho_invoice_number: purchaseData.zoho_invoice_number
      };

      const response = await apiClient.post(`/trials/${selectedTrial.id}/convert`, payload);
      toast.success("Trial converted to purchase!");
      setConvertDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert trial");
    }
  };

  const updateTrialStatus = async (trialId, status) => {
    try {
      await apiClient.put(`/trials/${trialId}`, { status });
      setTrials(trials.map(t => t.id === trialId ? { ...t, status } : t));
      toast.success("Trial status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const createZohoInvoice = async (purchase) => {
    try {
      toast.loading("Creating Zoho invoice...");
      const response = await apiClient.post(`/zoho/create-invoice/${purchase.id}`);
      toast.dismiss();
      if (response.data?.invoice_number) {
        toast.success(`Zoho Invoice created: ${response.data.invoice_number}`);
        fetchData();
      } else {
        toast.success("Invoice creation initiated");
        fetchData();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to create Zoho invoice");
    }
  };

  const resetForm = () => {
    setNewTrial({
      patient_id: "",
      clinic_id: clinics[0]?.id || "",
      hearing_aid_id: "",
      serial_number: "",
      ear: "both",
      trial_start_date: new Date().toISOString(),
      trial_days: 3,
      notes: ""
    });
  };

  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient?.name || "Unknown";
  };

  const getDaysRemaining = (trial) => {
    const endDate = new Date(trial.trial_end_date);
    const today = new Date();
    return differenceInDays(endDate, today);
  };

  const stats = {
    active: trials.filter(t => t.status === "in_progress").length,
    converted: trials.filter(t => t.status === "converted").length,
    returned: trials.filter(t => t.status === "returned").length,
    totalRevenue: purchases.reduce((sum, p) => sum + (p.total_amount || 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="trials-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Package className="w-7 h-7 text-amber-600" />
              Trial & Purchase Management
            </h1>
            <p className="text-slate-500">Track device trials, conversions, and sales</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-trial-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Trial
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Start Device Trial</DialogTitle>
                <DialogDescription>Assign a hearing aid for patient trial (3 days default)</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTrial} className="space-y-4 mt-4">
                <div>
                  <Label>Patient *</Label>
                  <Select value={newTrial.patient_id} onValueChange={(v) => setNewTrial({...newTrial, patient_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.phone})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Hearing Aid *</Label>
                  <Select value={newTrial.hearing_aid_id} onValueChange={(v) => setNewTrial({...newTrial, hearing_aid_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select device" /></SelectTrigger>
                    <SelectContent>
                      {hearingAids.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.brand} {a.model} - ₹{a.price?.toLocaleString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ear</Label>
                    <Select value={newTrial.ear} onValueChange={(v) => setNewTrial({...newTrial, ear: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Trial Days</Label>
                    <Input
                      type="number"
                      value={newTrial.trial_days}
                      onChange={(e) => setNewTrial({...newTrial, trial_days: parseInt(e.target.value) || 3})}
                      min={1}
                      max={30}
                    />
                  </div>
                </div>

                <div>
                  <Label>Serial Number</Label>
                  <Input
                    placeholder="Device serial number"
                    value={newTrial.serial_number}
                    onChange={(e) => setNewTrial({...newTrial, serial_number: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    placeholder="Trial instructions, special considerations..."
                    value={newTrial.notes}
                    onChange={(e) => setNewTrial({...newTrial, notes: e.target.value})}
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={!newTrial.patient_id || !newTrial.hearing_aid_id}>
                    Start Trial
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                <p className="text-sm text-slate-500">Active Trials</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.converted}</p>
                <p className="text-sm text-slate-500">Converted</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.returned}</p>
                <p className="text-sm text-slate-500">Returned</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">₹{(stats.totalRevenue/100000).toFixed(1)}L</p>
                <p className="text-sm text-slate-500">Total Revenue</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="trials">Trials ({trials.length})</TabsTrigger>
            <TabsTrigger value="purchases">Purchases ({purchases.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="trials" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-lg"></div>)}
                  </div>
                ) : trials.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {trials.map(trial => {
                      const daysLeft = getDaysRemaining(trial);
                      const statusInfo = TRIAL_STATUSES[trial.status] || TRIAL_STATUSES.in_progress;
                      
                      return (
                        <div key={trial.id} className="p-4 hover:bg-slate-50" data-testid={`trial-${trial.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                                <Ear className="w-6 h-6 text-amber-600" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-slate-900">{getPatientName(trial.patient_id)}</p>
                                  <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                                  {trial.status === "in_progress" && (
                                    <Badge variant="outline" className={daysLeft <= 0 ? "text-red-600 border-red-300" : "text-slate-600"}>
                                      {daysLeft <= 0 ? "Overdue" : `${daysLeft} days left`}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{trial.hearing_aid_name}</p>
                                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                  <span>Serial: {trial.serial_number || "N/A"}</span>
                                  <span>Ear: {trial.ear}</span>
                                  <span>Started: {format(new Date(trial.trial_start_date), "MMM d, yyyy")}</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {trial.status === "in_progress" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateTrialStatus(trial.id, "extended")}
                                  >
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Extend
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => {
                                      setSelectedTrial(trial);
                                      const aid = hearingAids.find(a => a.id === trial.hearing_aid_id);
                                      setPurchaseData({
                                        unit_price: aid?.price || 0,
                                        discount_percent: 0,
                                        zoho_invoice_number: ""
                                      });
                                      setConvertDialogOpen(true);
                                    }}
                                  >
                                    <ShoppingCart className="w-4 h-4 mr-1" />
                                    Convert
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => updateTrialStatus(trial.id, "returned")}
                                  >
                                    Return
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No trials yet</p>
                    <Button variant="link" onClick={() => setDialogOpen(true)}>Start your first trial</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="purchases" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {purchases.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {purchases.map(purchase => (
                      <div key={purchase.id} className="p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <ShoppingCart className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{getPatientName(purchase.patient_id)}</p>
                              <p className="text-sm text-slate-600">{purchase.hearing_aid_name}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                <span>Serial: {purchase.serial_number}</span>
                                <span>Zoho: {purchase.zoho_invoice_number || "Not created"}</span>
                                <span>{format(new Date(purchase.created_at), "MMM d, yyyy")}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-600">₹{purchase.total_amount?.toLocaleString()}</p>
                            <Badge className="bg-emerald-100 text-emerald-700">{purchase.status}</Badge>
                            {!purchase.zoho_invoice_id && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="mt-2 text-xs"
                                onClick={() => createZohoInvoice(purchase)}
                                data-testid={`create-invoice-${purchase.id}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Create Zoho Invoice
                              </Button>
                            )}
                            {purchase.zoho_invoice_id && (
                              <Badge className="mt-2 bg-blue-100 text-blue-700">
                                <FileText className="w-3 h-3 mr-1" />
                                {purchase.zoho_invoice_number}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No purchases yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Convert to Purchase Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convert Trial to Purchase</DialogTitle>
              <DialogDescription>
                {selectedTrial && `Converting trial for ${getPatientName(selectedTrial.patient_id)}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  value={purchaseData.unit_price}
                  onChange={(e) => setPurchaseData({...purchaseData, unit_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  value={purchaseData.discount_percent}
                  onChange={(e) => setPurchaseData({...purchaseData, discount_percent: parseFloat(e.target.value) || 0})}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <Label>Zoho Invoice Number</Label>
                <Input
                  placeholder="INV-001234"
                  value={purchaseData.zoho_invoice_number}
                  onChange={(e) => setPurchaseData({...purchaseData, zoho_invoice_number: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">Enter Zoho invoice number for reference</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  Total: <span className="font-bold text-emerald-600">
                    ₹{(purchaseData.unit_price * (1 - purchaseData.discount_percent / 100)).toLocaleString()}
                  </span>
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleConvertToPurchase}>
                  Complete Purchase
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
